import { CloseIcon } from "@cynthai/icons";
import { useResizeObserver } from "@reactuses/core";
import { useEffect, useRef, useState } from "react";
import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import Button from "../Button";

function CloseButton({ onClose }: { readonly onClose: () => void }) {
  return (
    <Button
      variant="default"
      size="square"
      aria-label="Close"
      onPress={() => {
        onClose();
      }}
    >
      <CloseIcon width={24} height={24} />
    </Button>
  );
}

const modalStyle = tv({
  base: `
    z-75 flex min-h-0 min-w-0 flex-auto items-center justify-center bg-background/50
    px-4 py-4 sm:py-10
  `,
  variants: {
    size: {
      sm: { modal: "max-w-sm" },
      md: { modal: "max-w-md" },
      lg: { modal: "max-w-lg" },
      xl: { modal: "max-w-xl" },
      "2xl": { modal: "max-w-2xl" },
      "3xl": { modal: "max-w-3xl" },
      "4xl": { modal: "max-w-4xl" },
      "5xl": { modal: "max-w-5xl" },
      "6xl": { modal: "max-w-6xl" },
      "7xl": { modal: "max-w-7xl" },
      "almost-full": { modal: "max-w-10/12" },
      "full-width": { modal: "w-full max-w-full px-4" },
    },
    fit: {
      wide: {
        dialog: "space-y-9 rounded-xl p-6",
      },
      narrow: {
        dialog: "space-y-1 rounded-xl p-6",
      },
    },
    alignment: {
      default: {},
      center: {
        title: "text-center",
        description: "text-center",
        header: "justify-center",
      },
    },
    footerAlignment: {
      start: { footer: "flex justify-start gap-2" },
      center: { footer: "flex justify-center gap-2" },
      end: { footer: "flex justify-end gap-2" },
    },
  },
  slots: {
    wrapper:
      "scroll-area mt-4 mb-6 flex min-h-0 flex-col gap-4 overflow-y-auto [scrollbar-gutter:stable]",
    dialog: `
      flex max-h-full w-full flex-col overflow-hidden border border-border bg-background-2 text-left align-middle
      shadow-modal
    `,
    header: "flex w-full shrink-0 flex-col items-start justify-between gap-1",
    footer: "shrink-0",
    title: "text-xl font-medium text-text-primary",
    description: "text-sm font-normal text-text-tertiary",
    modal: "flex max-h-full min-h-0 w-full flex-col overflow-hidden",
  },
  defaultVariants: {
    size: "2xl",
    alignment: "default",
    footerAlignment: "end",
    fit: "wide",
  },
});

interface BaseModalOwnProps extends VariantProps<typeof modalStyle> {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onOpenChange?: (isOpen: boolean) => void;
  readonly closeButton?: boolean;
  readonly children?: React.ReactNode | React.ReactNode[];
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly footer?: React.ReactNode | React.ReactNode[];
  readonly footerAlignment?: "start" | "center" | "end";
  readonly description?: React.ReactNode;
  readonly isDismissable?: boolean;
  readonly isKeyboardDismissDisabled?: boolean;
  readonly headerExtra?: React.ReactNode | React.ReactNode[];
  readonly classNames?: {
    dialog?: string;
    wrapper?: string;
    title?: string;
    description?: string;
    footer?: string;
    modal?: string;
  };
}

// a dialog always needs an accessible name: either it renders a title, or a
// caller rendering its own header names it through aria-label
type BaseModalNameProps =
  | {
      readonly title: NonNullable<React.ReactNode>;
      readonly "aria-label"?: string;
    }
  | { readonly title: null; readonly "aria-label": string };

export type BaseModalProps = BaseModalOwnProps & BaseModalNameProps;

const getModalOverlayStyle = (
  el: Element | undefined | null,
): React.CSSProperties | null => {
  if (!el) return null;

  const { top, left, width, height } = el.getBoundingClientRect();

  return {
    top,
    left,
    width,
    height,
    flexShrink: 0,
  };
};

export default function BaseModal({
  title: titleProp,
  "aria-label": ariaLabel,
  fit,
  closeButton = false,
  children,
  className,
  footer: footerProp,
  footerAlignment,
  style,
  description: descriptionProp,
  alignment,
  isDismissable = true,
  isKeyboardDismissDisabled,
  size,
  onClose,
  onOpenChange,
  classNames,
  isOpen,
  headerExtra,
}: BaseModalProps) {
  const [modalOverlayStyle, setModalOverlayStyle] =
    useState<React.CSSProperties | null>(null);

  const sidebarEl = useRef<Element>(document.getElementById("sidebar"));

  useEffect(() => {
    setModalOverlayStyle(getModalOverlayStyle(sidebarEl.current));
  }, []);

  useResizeObserver(sidebarEl, (entries) => {
    if (entries.length !== 0) {
      setModalOverlayStyle(getModalOverlayStyle(entries[0].target));
    }
  });

  const { wrapper, dialog, header, footer, title, description, base, modal } =
    modalStyle({
      size,
      fit,
      alignment,
      footerAlignment,
    });

  return (
    <ModalOverlay
      className={({ isEntering, isExiting }) =>
        twMerge(
          "fixed inset-0 z-40 flex h-(--visual-viewport-height) items-stretch justify-center",
          isEntering && "duration-300 ease-out",
          isExiting && "duration-200 ease-in",
        )
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      isDismissable={isDismissable}
    >
      {modalOverlayStyle && <div style={modalOverlayStyle}></div>}
      <div className={base()}>
        <Modal className={modal({ className: classNames?.modal })}>
          <Dialog
            className={dialog({ className: classNames?.dialog })}
            aria-label={ariaLabel}
          >
            <div slot="title" className={header()}>
              <div className="flex w-full items-center justify-between">
                {titleProp && (
                  <Heading
                    slot="title"
                    className={title({ className: classNames?.title })}
                  >
                    {titleProp}
                  </Heading>
                )}
                {closeButton && <CloseButton onClose={onClose} />}
              </div>

              {descriptionProp && (
                <div
                  className={description({
                    className: classNames?.description,
                  })}
                >
                  {descriptionProp}
                </div>
              )}

              {headerExtra}
            </div>
            {children && (
              <div className={wrapper({ className })} style={style}>
                {children}
              </div>
            )}

            {footerProp && (
              <footer className={footer({ className: classNames?.footer })}>
                {footerProp}
              </footer>
            )}
          </Dialog>
        </Modal>
      </div>
    </ModalOverlay>
  );
}
